import { Button } from 'components/universal/button';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Workplace } from 'views/employee-management/useEmployees';
import { DropdownIndicator } from '../addMultipleUser';
import { Input } from '../input';
import cls from './addNewUser.module.scss';
import { useAddNewUserForm } from './useAddNewUserForm';

export interface AddNewUserValue {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    employee_position_id: number | null;
    workplace_id?: string | number;
    department_id: string | number;
}

export type AddNewUserRequest = {
    data: AddNewUserValue;
};

interface AddNewUserFormInterface {
    adminWorkplaces: Workplace[];
    selectedWorkplaceId?: number;
    onCancel: (boolean) => void;
}

export const AddNewUserForm = ({ 
    adminWorkplaces,
    selectedWorkplaceId,
    onCancel 
}: AddNewUserFormInterface) => {
    const {
        t,
        form,
        workplaceId,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        selectedPosition,
        loadPositionOptions,
        setSelectedPosition,
        onSubmit,
        isSubmitting,
    } = useAddNewUserForm({ adminWorkplaces, onCancel, selectedWorkplaceId });

    const {
        register,
        control,
        formState: { errors },
        watch,
    } = form;

    return (
        <>
            <h3 className={cls.modalHeader}>
                {t('employeeManagement.addNewUser')}
            </h3>
            <p className={cls.modalDescription}>{t('employeeManagement.addNewUserModaldescriptionSingle')}</p>
            <form
                className={cls.form}
                onSubmit={form.handleSubmit(onSubmit)}
                noValidate
            >
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="first_name"
                    name="first_name"
                    label={t('registration.firstNameRequire')}
                    error={t(errors.first_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="last_name"
                    name="last_name"
                    label={t('registration.lastNameRequire')}
                    error={t(errors.last_name?.message)}
                    maxLength={255}
                />
                <div className={`${cls.input} ${cls.full}`}>
                    <label className={cls.label}>
                        {t('registration.positionRequire')}
                    </label>
                    <Controller
                        name="employee_position_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                className={errors.employee_position_id ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                value={selectedPosition}
                                loadOptions={loadPositionOptions}
                                additional={{ page: 1 }}
                                defaultOptions
                                onChange={(selected: { label: string; value: number } | null) => {
                                    field.onChange(selected?.value ?? null);
                                    setSelectedPosition(selected);
                                }}
                                noOptionsMessage={() => t('registration.noPositions')}
                                debounceTimeout={300}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    />
                    {errors.employee_position_id && (
                        <p className={cls.errorBox}>{t(errors.employee_position_id.message)}</p>
                    )}
                </div>
                <div className={cls.selectCustomWrapper}>
                    <p>{t('employeeManagement.workplace_idRequired')}</p>
                    <Controller
                        name="workplace_id"
                        control={control}
                        render={({ field }) => (
                            <Select
                                className="react-select-container"
                                options={adminWorkplaces}
                                getOptionLabel={(option: Workplace) => option.workplace_name}
                                getOptionValue={(option: Workplace) => option.id.toString()}
                                placeholder={t('employeeManagement.workplace_id')}
                                value={
                                    adminWorkplaces.find(w => w.id === Number(field.value)) ?? null
                                }
                                onChange={(option: Workplace | null) => {
                                        field.onChange(option?.id ?? '');
                                    }
                                }
                                components={{ DropdownIndicator }}
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    />
                </div>
                <div className={`${cls.selectCustomWrapper} ${workplaceId === 0 ? cls.disabled : ''}`}>
                    <label htmlFor="department_id" className={cls.label}>
                        {t('employeeManagement.departmentRequired')}
                    </label>
                    <Controller
                        name="department_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                key={workplaceId}
                                className={errors.department_id ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                isDisabled={workplaceId === 0}
                                value={selectedDepartment}
                                loadOptions={loadDepartmentOptions}
                                additional={{ page: 1 }}
                                onChange={(selected) => handleDepartmentChange(selected as any, field.onChange)}
                                noOptionsMessage={() => t('registration.noDepartments')}
                                debounceTimeout={300}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    />
                    {errors.department_id && (
                        <p className={cls.errorBox}>{t(errors.department_id.message).replace('department id', t('registration.firstLoginModal.department').toLowerCase())}</p>
                    )}
                </div>
                <div className={cls.modalButtonWrapper}>
                    <Button
                        className={cls.modalButton}
                        styleType="border"
                        type="button"
                        onClick={onCancel}
                    >
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send')}
                    </Button>
                </div>
            </form>
        </>
    );
};
